/**
 * Counts how artifact images fare, per museum, between reports.
 *
 * A results page renders dozens of images, so one analytics event per image
 * would be most of the site's event volume and still need summing to answer
 * the only question we ask of it: what share of images load, and how fast.
 * This keeps running totals instead, and `drain` hands them over as one row
 * per museum.
 *
 * Totals are deltas since the last drain. An image can start in one window
 * and settle in the next, so a single row may show more loaded than
 * requested; summed over any period the counts agree. Images that never
 * settle — the grid was replaced by a new search, or the visitor left first —
 * are the gap between requested and loaded + failed.
 */

export type ImageLoadCounts = {
  images_requested: number;
  /** Displayed, from either the mirror or the museum's own URL. */
  images_loaded: number;
  /** Every URL we had for it failed. */
  images_failed: number;
  /** Loaded, but only after the mirrored copy failed. */
  images_fallback: number;
  /** Sum over loaded images of start-to-load time, for an average. */
  image_load_ms: number;
};

export type ImageLoad = {
  /** Returns false if this image had already settled. */
  loaded(usedFallback: boolean): boolean;
  /** Returns false if this image had already settled. */
  failed(): boolean;
};

const emptyCounts = (): ImageLoadCounts => ({
  images_requested: 0,
  images_loaded: 0,
  images_failed: 0,
  images_fallback: 0,
  image_load_ms: 0,
});

export class ImageLoadTally {
  private counts = new Map<string, ImageLoadCounts>();

  constructor(private now: () => number) {}

  start(museum: string): ImageLoad {
    this.countsFor(museum).images_requested += 1;
    const startedAt = this.now();
    // Only the first outcome counts. A loaded image can still error later —
    // a mirrored copy purged mid-session — and that is not a second image.
    let settled = false;

    return {
      loaded: (usedFallback) => {
        if (settled) return false;
        settled = true;
        const counts = this.countsFor(museum);
        counts.images_loaded += 1;
        if (usedFallback) counts.images_fallback += 1;
        counts.image_load_ms += Math.round(this.now() - startedAt);
        return true;
      },
      failed: () => {
        if (settled) return false;
        settled = true;
        this.countsFor(museum).images_failed += 1;
        return true;
      },
    };
  }

  /** The counts since the last drain, one row per museum, and a fresh start. */
  drain(): Array<{ museum: string } & ImageLoadCounts> {
    const rows = Array.from(this.counts, ([museum, counts]) => ({
      museum,
      ...counts,
    }));
    this.counts.clear();
    return rows;
  }

  private countsFor(museum: string): ImageLoadCounts {
    let counts = this.counts.get(museum);
    if (!counts) {
      counts = emptyCounts();
      this.counts.set(museum, counts);
    }
    return counts;
  }
}
